import { NgModule, Component } from '@angular/core';
import {
  RouterModule,
  Routes,
  provideRouter,
  withViewTransitions,
} from '@angular/router';
import { PlaygroundComponent } from './pages/playground/playground.component';
import { CreatorRoutingModule } from './pages/creator/creator-routing.module';
import { PlayerLayoutRoutingModule } from './pages/player/player-layout-routing.module';
import { AuthRoutingModule } from './pages/auth/auth-routing.module';
import { SharingRoutingModule } from './pages/sharing/sharing-routing.module';
import { StorageExampleRoutingModule } from './pages/storage-example/storage-routing.module';
import { ProfileRoutingModule } from './pages/profile/profile-routing.module';
import { BlogRoutingModule } from './pages/blog/blog-routing.module';
import { CustomIdLoginGuard } from './core/guards/custom-id-login/custom-id-login.guard';
import { MyOmniverseRoutingModule } from './pages/creator/my-omniverse/my-omniverse-routing.module';
import { GamesmithGuard } from './core/guards/gamesmith.guard';
import { AuthGuard } from './core/guards/auth.guard';


@Component({
  template: ''
})
export class DiscordRedirectComponent {
  constructor() {
    // Only execute in browser environment
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Track Discord join conversion before redirect
      const mixpanel = (window as any)?.mixpanel;
      if (mixpanel) {
        mixpanel.track('join_discord', {
          source: 'direct_link',
          destination: 'https://discord.gg/adventureforge'
        });
        
        // Track user conversion
        mixpanel.track('user_conversion', {
          conversion_type: 'join_discord',
          referrer: document.referrer
        });
      }
      
      window.location.href = 'https://discord.gg/adventureforge';
    }
  }
}

export const routes: Routes = [
  // redirects
  { path: 'home', redirectTo: '', pathMatch: 'full' },
  { path: 'discord', component: DiscordRedirectComponent },

  // This is the main route
  {
    path: '',
    loadChildren: () =>
      import('./pages/home/home.module').then((m) => m.HomeModule),
  },

  // User tool modules
  {
    path: 'creator',
    canActivate: [CustomIdLoginGuard],
    loadChildren: () =>
      import('./pages/creator/creator-layout.module').then(
        (m) => m.CreatorLayoutModule,
      ),
  },
  {
    path: 'player',
    canActivate: [CustomIdLoginGuard],
    loadChildren: () =>
      import('./pages/player/player-layout.module').then(
        (m) => m.PlayerLayoutModule,
      ),
  },
  {
    path: 'profile',
    canActivate: [CustomIdLoginGuard],
    loadChildren: () =>
      import('./pages/profile/profile.module').then((m) => m.ProfileModule),
  },
  {
    path: 'subscription',
    canActivate: [CustomIdLoginGuard],
    loadComponent: () =>
      import('./pages/subscription/subscription.component').then(
        (m) => m.SubscriptionComponent,
      ),
  },

  // Authentication module
  {
    path: 'auth',
    canActivate: [CustomIdLoginGuard],
    loadChildren: () =>
      import('./pages/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'username',
    canActivate: [CustomIdLoginGuard],
    loadChildren: () =>
      import('./pages/auth/register-username/register-username.module').then(
        (m) => m.RegisterUsernameModule,
      ),
  },

  // Outside modules
  {
    canActivate: [CustomIdLoginGuard],
    path: 'notifications',
    loadChildren: () =>
      import('./pages/notifications/notifications.module').then(
        (m) => m.NotificationsModule,
      ),
  },
  {
    canActivate: [CustomIdLoginGuard, GamesmithGuard],
    path: 'recycle-bin',
    loadChildren: () =>
      import('./pages/recycle-bin/recycle-bin.module').then(
        (m) => m.RecycleBinModule,
      ),
  },
  {
    path: 'sharing',
    loadChildren: () =>
      import('./pages/sharing/sharing.module').then((m) => m.SharingModule),
  },

  // Social
  {
    path: 'news',
    loadChildren: () =>
      import('./pages/news/news.module').then((m) => m.NewsModule),
  },

  {
    path: 'game-jam-2024-col',
    loadChildren: () =>
      import('./pages/game-jam-2024-col/game-jam-2024-col.module').then(
        (m) => m.GameJamColModule,
      ),
  },

  {
    path: 'sea-of-legends',
    loadChildren: () =>
      import('./pages/sea-of-legends/sea-of-legends.module').then(
        (m) => m.SeaOfLegendsModule,
      ),
  },

  {
    path: 'seaoflegends',
    loadChildren: () =>
      import('./pages/sea-of-legends/sea-of-legends.module').then(
        (m) => m.SeaOfLegendsModule,
      ),
  },
  {
    path: 'learning',
    canActivate: [AuthGuard],

    loadChildren: () =>
      import('./pages/learning/learning.module').then((m) => m.LearningModule),
  },
  {
    path: 'support',
    loadChildren: () =>
      import('./pages/support/support.module').then((m) => m.SupportModule),
  },
  {
    path: 'blog',
    loadChildren: () =>
      import('./pages/blog/blog.module').then((m) => m.BlogModule),
  },
  {
    path: 'email-verified',
    loadChildren: () =>
      import('./pages/email-verified/email-verified.module').then(
        (m) => m.EmailVerifiedModule,
      ),
  },
  // Test
  { path: 'blog/welcome-to-adventure-forge', component: PlaygroundComponent },

  // this have to be here at the end
  {
    path: '404',
    loadChildren: () =>
      import('./pages/page-not-found/page-not-found.module').then(
        (m) => m.PageNotFoundModule,
      ),
  },

  {
    path: 'policies',
    loadChildren: () =>
      import('./pages/policies/policies.module').then((m) => m.PoliciesModule),
  },
  {
    path: 'next-fest/:customId',
    loadChildren: () =>
      import('./pages/next-fest/next-fest.module').then(
        (m) => m.NextFestModule,
      ),
  },

  {
    path: 'terms-of-service',
    loadChildren: () =>
      import('./pages/terms-of-service/terms-of-service.module').then(
        (m) => m.TermsOfServiceModule,
      ),
  },
  {
    path: 'playground',
    loadChildren: () =>
      import('./pages/playground/playground.module').then(
        (m) => m.PlaygroundModule,
      ),
  },

  // Test
  { path: 'blog/welcome-to-adventure-forge', component: PlaygroundComponent },

  // this have to be here at the end
  {
    path: '404',
    loadChildren: () =>
      import('./pages/page-not-found/page-not-found.module').then(
        (m) => m.PageNotFoundModule,
      ),
  },
  {
    path: '**',
    loadChildren: () =>
      import('./pages/page-not-found/page-not-found.module').then(
        (m) => m.PageNotFoundModule,
      ),
  },
];

@NgModule({
  imports: [
    // RouterModule.forRoot(routes),
    // RouterModule.forRoot(routes, { enableTracing: true }), // Debug purpose: Track route behaviour

    // IMPORTANT: Don't forget to add the new routing module if you create a new one
    AuthRoutingModule,
    CreatorRoutingModule,
    PlayerLayoutRoutingModule,
    SharingRoutingModule,
    StorageExampleRoutingModule,
    ProfileRoutingModule,
    BlogRoutingModule,
    CreatorRoutingModule,
    // NextFestRoutingModule,
  ],
  exports: [RouterModule],
  providers: [provideRouter(routes, withViewTransitions())],
})
export class AppRoutingModule { }
